import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Badge, InputGroup } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Users.module.css';

const Users = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const { getToken, user: currentUser, recarregarPermissoes } = useAuth();

  // ── Permissões granulares ──
  const [modulosDisponiveis, setModulosDisponiveis] = useState([]);
  const [showPermModal, setShowPermModal] = useState(false);
  const [permTarget, setPermTarget] = useState(null);
  const [permSelecionadas, setPermSelecionadas] = useState(new Set());
  const [permLoading, setPermLoading] = useState(false);
  const [permError, setPermError] = useState('');
  const [permSaving, setPermSaving] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    role: 'user',
    ativo: true
  });

  const fetchUsers = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        setError("Autenticação necessária.");
        setLoading(false);
        return;
      }
      const response = await fetch('/api/usuarios', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        // A API retorna o array de usuários diretamente
        const usersList = Array.isArray(data) ? data : (data.data || []);
        setUsuarios(usersList);
        setFilteredUsers(usersList);
      } else {
        setError(data.message || 'Erro ao carregar usuários');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Carregar catálogo de módulos (uma vez)
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch('/api/usuarios/modulos-disponiveis', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success && Array.isArray(d.data)) setModulosDisponiveis(d.data); })
      .catch(() => {});
  }, [getToken]);

  // Abrir modal de permissões
  const abrirPermissoes = async (usuario) => {
    setPermTarget(usuario);
    setPermError('');
    setPermLoading(true);
    setPermSelecionadas(new Set());
    setShowPermModal(true);
    try {
      const r = await fetch(`/api/usuarios/${usuario.id}/permissoes`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const d = await r.json();
      if (d.success && Array.isArray(d.data?.modulos)) {
        setPermSelecionadas(new Set(d.data.modulos));
      } else {
        setPermError(d.message || 'Erro ao carregar permissões');
      }
    } catch (e) {
      setPermError('Erro de conexão');
    } finally {
      setPermLoading(false);
    }
  };

  const togglePermissao = (moduloId) => {
    setPermSelecionadas(prev => {
      const novo = new Set(prev);
      if (novo.has(moduloId)) novo.delete(moduloId);
      else novo.add(moduloId);
      return novo;
    });
  };

  const salvarPermissoes = async () => {
    if (!permTarget) return;
    setPermSaving(true);
    setPermError('');
    try {
      const r = await fetch(`/api/usuarios/${permTarget.id}/permissoes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ modulos: [...permSelecionadas] })
      });
      const d = await r.json();
      if (d.success) {
        // Se alterou o próprio usuário, recarregar permissões locais
        if (currentUser && permTarget.id === currentUser.id) {
          await recarregarPermissoes?.();
        }
        setShowPermModal(false);
      } else {
        setPermError(d.message || 'Erro ao salvar');
      }
    } catch (e) {
      setPermError('Erro de conexão');
    } finally {
      setPermSaving(false);
    }
  };

  // Filtrar usuários
  useEffect(() => {
    let filtered = usuarios;

    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de role
    if (filterRole !== 'all') {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    // Filtro de status
    if (filterStatus !== 'all') {
      const isActive = filterStatus === 'active';
      filtered = filtered.filter(user => user.ativo === isActive);
    }

    setFilteredUsers(filtered);
  }, [searchTerm, filterRole, filterStatus, usuarios]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.nome || !formData.email) {
      setError('Nome e email são obrigatórios');
      return;
    }

    if (!editingUser && (!formData.senha || formData.senha.length < 6)) {
      setError('Senha é obrigatória e deve ter no mínimo 6 caracteres');
      return;
    }

    const url = editingUser
      ? `/api/usuarios/${editingUser.id}`
      : '/api/usuarios';
    const method = editingUser ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        closeModal();
        fetchUsers();
      } else {
        setError(data.message || 'Erro ao salvar usuário');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor');
    }
  };

  const handleEdit = (usuario) => {
    setEditingUser(usuario);
    setFormData({
      nome: usuario.nome,
      email: usuario.email,
      senha: '',
      role: usuario.role,
      ativo: usuario.ativo === 1 || usuario.ativo === true
    });
    setShowModal(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este usuário?')) {
      try {
        const token = getToken();
        const response = await fetch(`/api/usuarios/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (response.ok) {
          fetchUsers();
        } else {
          alert(data.message || 'Erro ao deletar usuário');
        }
      } catch (err) {
        alert('Erro ao conectar com o servidor');
      }
    }
  };

  const handleResetSenha = async (id, nome) => {
    if (window.confirm(`Deseja resetar a senha de ${nome} para 'gringao123'?\nO usuário será obrigado a trocar a senha no próximo login.`)) {
      try {
        const token = getToken();
        // Usar api client se existir ou fetch direto como no handleDelete
        const response = await fetch(`/api/usuarios/${id}/reset-senha`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (response.ok) {
          alert(data.message);
        } else {
          alert(data.message || 'Erro ao resetar senha');
        }
      } catch (err) {
        console.error('Erro ao resetar senha:', err);
        alert('Erro ao conectar com o servidor');
      }
    }
  };

  const openNewUserModal = () => {
    setEditingUser(null);
    setFormData({ nome: '', email: '', senha: '', role: 'user', ativo: true });
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ nome: '', email: '', senha: '', role: 'user', ativo: true });
    setError('');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">👥 Gerenciamento de Usuários</h2>
              <p className="text-muted mb-0">Gerencie os usuários do sistema</p>
            </div>
            <Button variant="primary" size="lg" onClick={openNewUserModal}>
              <i className="bi bi-plus-circle me-2"></i>
              Novo Usuário
            </Button>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col xs={6} md={4} lg>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div className="text-primary mb-2" style={{ fontSize: '2rem' }}>👤</div>
              <h3 className="mb-0">{usuarios.length}</h3>
              <small className="text-muted">Total de Usuários</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4} lg>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div className="text-success mb-2" style={{ fontSize: '2rem' }}>✓</div>
              <h3 className="mb-0">{usuarios.filter(u => u.ativo).length}</h3>
              <small className="text-muted">Usuários Ativos</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4} lg>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div className="text-warning mb-2" style={{ fontSize: '2rem' }}>⭐</div>
              <h3 className="mb-0">{usuarios.filter(u => u.role === 'admin').length}</h3>
              <small className="text-muted">Administradores</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={6} lg>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div className="text-info mb-2" style={{ fontSize: '2rem' }}>👥</div>
              <h3 className="mb-0">{usuarios.filter(u => u.role === 'user').length}</h3>
              <small className="text-muted">Usuários Comuns</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={6} lg>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div className="text-secondary mb-2" style={{ fontSize: '2rem' }}>🏭</div>
              <h3 className="mb-0">{usuarios.filter(u => u.role === 'fornecedor').length}</h3>
              <small className="text-muted">Fornecedores</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="all">Todas as Funções</option>
                <option value="admin">Administradores</option>
                <option value="user">Usuários</option>
                <option value="fornecedor">Fornecedores</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">Todos os Status</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Users Grid */}
      <Row>
        {filteredUsers.map((usuario) => (
          <Col key={usuario.id} md={6} lg={4} className="mb-4">
            <Card className={`h-100 shadow-sm ${styles.userCard}`}>
              <Card.Body>
                <div className="d-flex align-items-start mb-3">
                  <div
                    className={styles.avatar}
                    style={{ backgroundColor: getAvatarColor(usuario.nome) }}
                  >
                    {getInitials(usuario.nome)}
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h5 className="mb-1">{usuario.nome}</h5>
                    <p className="text-muted mb-2 small">{usuario.email}</p>
                    <div className="d-flex gap-2">
                      <Badge bg={usuario.role === 'admin' ? 'warning' : usuario.role === 'fornecedor' ? 'secondary' : 'primary'}>
                        {usuario.role === 'admin' ? '⭐ Admin' : usuario.role === 'fornecedor' ? '🏭 Fornecedor' : '👤 Usuário'}
                      </Badge>
                      <Badge bg={usuario.ativo ? 'success' : 'secondary'}>
                        {usuario.ativo ? '✓ Ativo' : '✗ Inativo'}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="border-top pt-3">
                  <div className="d-flex justify-content-between text-muted small mb-2">
                    <span>Criado em:</span>
                    <strong>{new Date(usuario.data_criacao).toLocaleDateString('pt-BR')}</strong>
                  </div>
                  <div className="d-flex justify-content-between text-muted small">
                    <span>Último acesso:</span>
                    <strong>
                      {usuario.ultimo_acesso
                        ? new Date(usuario.ultimo_acesso).toLocaleDateString('pt-BR')
                        : 'Nunca'}
                    </strong>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="flex-grow-1"
                    onClick={() => handleEdit(usuario)}
                  >
                    ✏️ Editar
                  </Button>
                  <Button
                    variant="outline-info"
                    size="sm"
                    title="Permissões de acesso aos módulos"
                    onClick={() => abrirPermissoes(usuario)}
                  >
                    🔒
                  </Button>
                  {currentUser && usuario.id !== currentUser.id && (
                    <>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        title="Resetar Senha"
                        onClick={() => handleResetSenha(usuario.id, usuario.nome)}
                      >
                        🔑
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(usuario.id)}
                      >
                        🗑️
                      </Button>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {filteredUsers.length === 0 && !loading && (
        <Card className="text-center py-5">
          <Card.Body>
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>👥</div>
            <h4 className="text-muted">Nenhum usuário encontrado</h4>
            <p className="text-muted">
              {searchTerm || filterRole !== 'all' || filterStatus !== 'all'
                ? 'Tente ajustar os filtros'
                : 'Clique em "Novo Usuário" para começar'}
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? '✏️ Editar Usuário' : '➕ Novo Usuário'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <span className="me-2">⚠️</span>
                <div>{error}</div>
              </div>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nome Completo *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    placeholder="Nome do usuário"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="email@exemplo.com"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Senha {editingUser ? '(deixe em branco para manter)' : '*'}
              </Form.Label>
              <Form.Control
                type="password"
                name="senha"
                value={formData.senha}
                onChange={handleInputChange}
                placeholder="Mínimo 6 caracteres"
                required={!editingUser}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Função</Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                  >
                    <option value="user">👤 Usuário</option>
                    <option value="admin">⭐ Administrador</option>
                    <option value="fornecedor">🏭 Fornecedor</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Check
                    type="switch"
                    id="ativo-switch"
                    name="ativo"
                    label={formData.ativo ? '✓ Usuário Ativo' : '✗ Usuário Inativo'}
                    checked={formData.ativo}
                    onChange={handleInputChange}
                    className="mt-2"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2 justify-content-end mt-4">
              <Button variant="secondary" onClick={closeModal}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit">
                {editingUser ? '💾 Salvar Alterações' : '➕ Criar Usuário'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal de Permissões Granulares */}
      <Modal show={showPermModal} onHide={() => setShowPermModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            🔒 Permissões — {permTarget?.nome}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Marque os módulos que <strong>{permTarget?.nome}</strong> pode acessar.
            Mudanças são auditadas. Admin não tem acesso automático — todos os módulos
            precisam ser concedidos explicitamente.
          </p>

          {permError && (
            <div className="alert alert-danger small">⚠️ {permError}</div>
          )}

          {permLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
              <div className="mt-2 small text-muted">Carregando permissões...</div>
            </div>
          ) : (
            <>
              <div className="d-flex gap-2 mb-3">
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={() => setPermSelecionadas(new Set(modulosDisponiveis.map(m => m.id)))}
                >
                  ✓ Conceder todos
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setPermSelecionadas(new Set())}
                >
                  ✕ Limpar todos
                </Button>
                <div className="ms-auto small text-muted align-self-center">
                  {permSelecionadas.size} / {modulosDisponiveis.length} módulos
                </div>
              </div>

              <Row>
                {modulosDisponiveis.map(m => {
                  const ativo = permSelecionadas.has(m.id);
                  return (
                    <Col md={6} key={m.id} className="mb-2">
                      <Card
                        onClick={() => togglePermissao(m.id)}
                        style={{
                          cursor: 'pointer',
                          background: ativo ? '#e7f5ff' : '#fff',
                          borderColor: ativo ? '#1971c2' : (m.critico ? '#fab005' : '#dee2e6'),
                          borderWidth: m.critico ? 2 : 1
                        }}
                      >
                        <Card.Body className="py-2 px-3 d-flex align-items-start gap-2">
                          <Form.Check
                            type="checkbox"
                            checked={ativo}
                            onChange={() => {}}
                            onClick={e => e.stopPropagation()}
                            className="mt-1"
                          />
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ fontSize: '1.1rem' }}>{m.icone}</span>
                              <strong>{m.label}</strong>
                              {m.critico && (
                                <Badge bg="warning" text="dark" pill style={{ fontSize: '0.65rem' }}>
                                  CRÍTICO
                                </Badge>
                              )}
                            </div>
                            <div className="text-muted small">{m.descricao}</div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPermModal(false)} disabled={permSaving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={salvarPermissoes} disabled={permLoading || permSaving}>
            {permSaving ? '💾 Salvando...' : '💾 Salvar Permissões'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Users;
